import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type {
  ColumnDateFormat,
  ColumnMeta,
  ColumnOverrides,
  ColumnOverrideType,
} from '@/types/dataset';
import { getQueryWorker } from '@/engine/worker-client';
import { useDatasetStore } from '@/store/dataset-store';
import { configDb } from '@/persistence/config-db';
import { kindOf } from '@/features/filters/column-types';
import { useFocusTrap } from '@/hooks/useFocusTrap';

type Props = {
  datasetId: string;
  columns: readonly ColumnMeta[];
  current: ColumnOverrides;
  onClose: () => void;
};

// Date format strings are pattern templates — they look the same in TR/EN
// (the letters are mnemonic, not locale-bound). Kept as a module constant.
const DATE_FORMAT_LABELS: Record<ColumnDateFormat, string> = {
  iso: 'YYYY-MM-DD',
  tr_dot: 'GG.AA.YYYY',
  tr_slash: 'GG/AA/YYYY',
  tr_dash: 'GG-AA-YYYY',
};

/**
 * Modal for overriding auto-detected column types. Changes are staged until
 * "Uygula" — on save the typed view is rebuilt, Dexie is updated, and the
 * dataset store is re-primed with fresh column metadata.
 */
export function ColumnSettingsModal({
  datasetId,
  columns,
  current,
  onClose,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const trapRef = useFocusTrap();
  const applyOverrides = useDatasetStore((s) => s.applyOverrides);
  const setDatasetProfile = useDatasetStore((s) => s.setDatasetProfile);
  const [draft, setDraft] = useState<ColumnOverrides>(() => ({ ...current }));
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return columns;
    return columns.filter((c) => c.name.toLowerCase().includes(q));
  }, [columns, search]);

  function setType(col: string, next: 'auto' | ColumnOverrideType): void {
    setDraft((prev) => {
      const copy = { ...prev };
      if (next === 'auto') {
        delete copy[col];
      } else if (next === 'text') {
        copy[col] = { type: 'text' };
      } else if (next === 'numeric') {
        copy[col] = { type: 'numeric' };
      } else {
        copy[col] = { type: 'date', dateFormat: 'iso' };
      }
      return copy;
    });
  }

  function setTrNumeric(col: string, value: boolean): void {
    setDraft((prev) => {
      const existing = prev[col];
      if (!existing || existing.type !== 'numeric') return prev;
      return {
        ...prev,
        [col]: value ? { type: 'numeric', trNumeric: true } : { type: 'numeric' },
      };
    });
  }

  function setDateFormat(col: string, fmt: ColumnDateFormat): void {
    setDraft((prev) => {
      const existing = prev[col];
      if (!existing || existing.type !== 'date') return prev;
      return { ...prev, [col]: { type: 'date', dateFormat: fmt } };
    });
  }

  async function apply(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const api = getQueryWorker();
      const preview = await api.applyColumnOverrides(datasetId, draft);
      await configDb.columnOverrides.put({
        datasetId,
        overrides: draft,
        updatedAt: Date.now(),
      });
      applyOverrides(datasetId, preview, draft);
      // Background re-profile (matches UploadButton pattern).
      api
        .profileDataset(datasetId)
        .then((profile) => setDatasetProfile(datasetId, profile))
        .catch((err: unknown) => {
          console.warn('profileDataset (post-override) failed', err);
        });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div
      ref={trapRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 outline-none"
      style={{ background: 'rgba(0,0,0,0.45)' }}
    >
      <div
        className="w-[560px] max-h-[82vh] flex flex-col gap-3 p-4 rounded-lg shadow-xl"
        style={{
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
        }}
      >
        <div className="flex items-center justify-between">
          <h2
            className="text-sm font-semibold"
            style={{ color: 'var(--color-fg)' }}
          >
            {t('columnSettings.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="text-lg px-2 rounded hover:opacity-80"
            style={{ color: 'var(--color-muted)' }}
          >
            ×
          </button>
        </div>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.searchColumn')}
          className="text-xs p-1.5 rounded"
          style={{ border: '1px solid var(--color-border)' }}
          autoFocus
        />

        <div
          className="flex flex-col overflow-auto min-h-[200px] max-h-[440px] p-1 rounded"
          style={{ border: '1px solid var(--color-border)' }}
        >
          {filtered.length === 0 && (
            <div
              className="text-xs p-2"
              style={{ color: 'var(--color-muted)' }}
            >
              {t('common.noMatch')}
            </div>
          )}
          {filtered.map((c) => {
            const override = draft[c.name];
            const selected: 'auto' | ColumnOverrideType =
              override?.type ?? 'auto';
            return (
              <div
                key={c.name}
                className="flex items-center gap-2 px-1.5 py-1 text-xs"
                style={{ color: 'var(--color-fg)' }}
              >
                <div className="flex-1 min-w-0">
                  <div className="truncate" title={c.name}>
                    {c.name}
                  </div>
                  <div
                    className="text-[10px] uppercase"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {kindOf(c.sqlType)} · {c.sqlType}
                  </div>
                </div>

                <select
                  value={selected}
                  onChange={(e) =>
                    setType(c.name, e.target.value as 'auto' | ColumnOverrideType)
                  }
                  className="text-xs p-1 rounded"
                  style={{ border: '1px solid var(--color-border)' }}
                  aria-label={t('columnSettings.typeAria', { column: c.name })}
                >
                  {(['auto', 'text', 'numeric', 'date'] as const).map((k) => (
                    <option key={k} value={k}>
                      {t(`columnSettings.types.${k}`)}
                    </option>
                  ))}
                </select>

                {override?.type === 'numeric' && (
                  <label
                    className="flex items-center gap-1 text-[10px] cursor-pointer select-none"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    <input
                      type="checkbox"
                      checked={override.trNumeric ?? false}
                      onChange={(e) =>
                        setTrNumeric(c.name, e.target.checked)
                      }
                    />
                    1.234,56
                  </label>
                )}

                {override?.type === 'date' && (
                  <select
                    value={override.dateFormat}
                    onChange={(e) =>
                      setDateFormat(
                        c.name,
                        e.target.value as ColumnDateFormat,
                      )
                    }
                    className="text-xs p-1 rounded"
                    style={{ border: '1px solid var(--color-border)' }}
                    aria-label={t('columnSettings.dateFormatAria', { column: c.name })}
                  >
                    {(
                      Object.keys(DATE_FORMAT_LABELS) as ColumnDateFormat[]
                    ).map((f) => (
                      <option key={f} value={f}>
                        {DATE_FORMAT_LABELS[f]}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            );
          })}
        </div>

        {error && (
          <div
            className="text-xs p-2 rounded"
            role="alert"
            style={{
              color: 'var(--color-danger)',
              border: '1px solid var(--color-danger)',
            }}
          >
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-xs px-3 py-1 rounded disabled:opacity-60"
            style={{ color: 'var(--color-muted)' }}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              void apply();
            }}
            disabled={busy}
            className="text-xs px-3 py-1 rounded text-white disabled:opacity-60"
            style={{ background: 'var(--color-accent)' }}
          >
            {busy ? t('common.applying') : t('common.apply')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
